import PropTypes from "prop-types";
import { useState } from "react";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaChevronDown,
  FaChevronUp,
  FaItalic,
  FaUnderline,
} from "react-icons/fa";
import "../styles.css";

const TextEditor = ({
  selectedText,
  updateTextProperties,
  setSelectedTextId,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!selectedText) return null;

  const toggleStyle = (style) => {
    const currentStyle = selectedText.fontStyle || "";
    const styles = currentStyle.split(" ").filter((s) => s);
    const hasStyle = styles.includes(style);

    let newStyle;
    if (hasStyle) {
      newStyle = styles.filter((s) => s !== style).join(" ");
    } else {
      styles.push(style);
      newStyle = styles.join(" ");
    }

    // Apply the style to the selected text
    if (selectedText.range && selectedText.inputType) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(selectedText.range);
      }
    }

    updateTextProperties(selectedText.id, { fontStyle: newStyle });
  };

  const handleDone = () => {
    // Clear the selection before closing
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
    setSelectedTextId(null);
  };

  return (
    <div className={`text-properties-panel ${isCollapsed ? "collapsed" : ""}`}>
      <div className="panel-header">
        <h3 className="panel-title">Text Properties</h3>
        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
        </button>
      </div>

      <div className="panel-content">
        {/* Font Family */}
        <label className="label">Font Family</label>
        <select
          value={selectedText.fontFamily || "Roboto"}
          onChange={(e) =>
            updateTextProperties(selectedText.id, {
              fontFamily: e.target.value,
            })
          }
          className="input-select"
        >
          <option value="Roboto">Roboto</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
        </select>

        {/* Font Size */}
        <label className="label">Font Size</label>
        <input
          type="number"
          value={selectedText.fontSize || 14}
          min={10}
          max={100}
          onChange={(e) =>
            updateTextProperties(selectedText.id, {
              fontSize: parseInt(e.target.value, 10),
            })
          }
          className="input-field"
        />

        {/* Font Color */}
        <label className="label">Font Color</label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              width: "30px",
              height: "30px",
              backgroundColor:
                selectedText.fill || selectedText.color || "#000000",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          <input
            type="color"
            value={selectedText.fill || selectedText.color || "#000000"}
            onChange={(e) =>
              updateTextProperties(selectedText.id, {
                fill: e.target.value,
                color: e.target.value,
              })
            }
            className="color-picker"
            style={{ flex: 1 }}
          />
        </div>

        {/* Text Style */}
        <label className="label">Text Style</label>
        <div className="button-group">
          <button
            onClick={() => toggleStyle("bold")}
            className={`format-btn ${
              selectedText.fontStyle?.includes("bold") ? "active" : ""
            }`}
            title="Bold"
          >
            <FaBold />
          </button>

          <button
            onClick={() => toggleStyle("italic")}
            className={`format-btn ${
              selectedText.fontStyle?.includes("italic") ? "active" : ""
            }`}
            title="Italic"
          >
            <FaItalic />
          </button>

          <button
            onClick={() => {
              if (selectedText.range && selectedText.inputType) {
                const selection = window.getSelection();
                if (selection) {
                  selection.removeAllRanges();
                  selection.addRange(selectedText.range);
                }
              }
              updateTextProperties(selectedText.id, {
                underline: !selectedText.underline,
                fontStyle: selectedText.fontStyle || "",
              });
            }}
            className={`format-btn ${selectedText.underline ? "active" : ""}`}
            title="Underline"
          >
            <FaUnderline />
          </button>
        </div>

        {/* Text Alignment */}
        <label className="label">Text Alignment</label>
        <div className="button-group">
          <button
            onClick={() =>
              updateTextProperties(selectedText.id, { align: "left" })
            }
            className={`format-btn ${
              selectedText.align === "left" ? "active" : ""
            }`}
            title="Align Left"
          >
            <FaAlignLeft />
          </button>
          <button
            onClick={() =>
              updateTextProperties(selectedText.id, { align: "center" })
            }
            className={`format-btn ${
              selectedText.align === "center" ? "active" : ""
            }`}
            title="Align Center"
          >
            <FaAlignCenter />
          </button>
          <button
            onClick={() =>
              updateTextProperties(selectedText.id, { align: "right" })
            }
            className={`format-btn ${
              selectedText.align === "right" ? "active" : ""
            }`}
            title="Align Right"
          >
            <FaAlignRight />
          </button>
        </div>

        {/* Done Button */}
        <button onClick={handleDone} className="apply-btn">
          Done
        </button>
      </div>
    </div>
  );
};

TextEditor.propTypes = {
  selectedText: PropTypes.shape({
    id: PropTypes.any,
    fontFamily: PropTypes.string,
    fontSize: PropTypes.number,
    fill: PropTypes.string,
    fontStyle: PropTypes.string,
    underline: PropTypes.bool,
    align: PropTypes.string,
  }),
  updateTextProperties: PropTypes.func.isRequired,
  setSelectedTextId: PropTypes.func.isRequired,
};

export default TextEditor;
